// Figma: "Loyalty Cloud" file, node 18093:65 ("Profile / Default") — Settings shell with a
// left nav (back to the dashboard, Profile/Security/Members destinations) and a top bar
// with a breadcrumb label + notification/profile buttons. Mirrors MainLayout's Sidebar/
// TopHeader split, but as its own shell since the destinations and back-link are settings-
// specific rather than account/community/campaign-scoped.
import React from 'react';
import {useSelector} from 'react-redux';
import {Box, ButtonBase, Stack, Typography} from '@mui/material';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';

import {tokens} from '../../themes/ThemeCustomization';
import {getAccountSfid} from '../../app/store/reducers/session';
// NOTE: filenames reflect the Figma asset-export order, not their actual shape — the
// export batch was mislabeled. `back-arrow.svg` is really a shield (Security), `profile.svg`
// is really a left chevron (Settings' back-link), `security.svg` is really a bell
// (notifications), and `bell.svg`/`user.svg` are both the single-person "Profile" glyph.
import shieldIcon from '../../assets/icons/settings/back-arrow.svg';
import chevronLeftIcon from '../../assets/icons/settings/profile.svg';
import bellIcon from '../../assets/icons/settings/security.svg';
import membersIcon from '../../assets/icons/settings/members.svg';
import profileIcon from '../../assets/icons/settings/bell.svg';

const NAV_ITEMS = [
    {id: 'profile', label: 'Profile', icon: profileIcon, path: '/settings/profile'},
    {id: 'security', label: 'Security', icon: shieldIcon, path: '/settings/security'},
    {id: 'members', label: 'Members', title: 'Team Members', icon: membersIcon, path: '/settings/members'}
];

const SettingsLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const sessionAccountSfid = useSelector(getAccountSfid);

    // Same fallback IndexRedirect (routes/AppRoutes.js) uses for '/' — settings has no
    // account/community/campaign params of its own to derive a destination from.
    const handleBack = () => {
        // From the Invite Members form, go back to the member list first.
        if (location.pathname.startsWith('/settings/members') && new URLSearchParams(location.search).get('invite') === '1') {
            navigate('/settings/members');
            return;
        }
        const storedAccountSfid = localStorage.getItem('account_sfid');
        const accountSfid = storedAccountSfid || sessionAccountSfid;
        navigate(accountSfid ? `/account/${accountSfid}` : '/');
    };

    return (
        <Box sx={{display: 'flex', width: '100%', minHeight: '100vh'}}>
            <Box
                component="nav"
                sx={{
                    width: '255px',
                    minWidth: '240px',
                    height: '100vh',
                    position: 'sticky',
                    top: 0,
                    flexShrink: 0,
                    backgroundColor: tokens.color.neutral50,
                    borderRight: `1px solid ${tokens.color.neutral200}`,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box sx={{borderBottom: `1px solid ${tokens.color.neutral200}`, pt: 0.5, pb: 1, px: 1}}>
                    <ButtonBase
                        onClick={handleBack}
                        sx={{width: '100%', height: 40, borderRadius: tokens.radius.control, justifyContent: 'flex-start', gap: 1, pl: 0.25}}
                    >
                        <Box sx={{width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <Box component="img" src={chevronLeftIcon} alt="" sx={{width: 16, height: 16}} />
                        </Box>
                        <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral600}}>
                            Settings
                        </Typography>
                    </ButtonBase>
                </Box>

                <Stack sx={{pt: 1, px: 1, gap: '1px'}}>
                    {NAV_ITEMS.map((item) => {
                        const isActive = location.pathname.startsWith(item.path);
                        return (
                            <ButtonBase
                                key={item.id}
                                onClick={() => navigate(item.path)}
                                sx={{
                                    height: 36,
                                    borderRadius: tokens.radius.control,
                                    justifyContent: 'flex-start',
                                    gap: 1,
                                    pl: 1.25,
                                    pr: 1,
                                    backgroundColor: isActive ? tokens.color.neutral200 : 'transparent',
                                    '&:hover': {backgroundColor: isActive ? tokens.color.neutral200 : tokens.color.neutral100}
                                }}
                            >
                                <Box component="img" src={item.icon} alt="" sx={{width: 16, height: 16}} />
                                <Typography variant="body2" fontWeight={500} sx={{color: isActive ? tokens.color.neutral950 : tokens.color.neutral600}}>
                                    {item.label}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Stack>
            </Box>

            <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column'}}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        height: '57px',
                        px: 2,
                        backgroundColor: tokens.color.neutral50,
                        borderBottom: `1px solid ${tokens.color.neutral200}`,
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                    }}
                >
                    <Box sx={{width: 36}} />
                    <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.neutral950}}>
                        {NAV_ITEMS.find((item) => location.pathname.startsWith(item.path))?.title ?? NAV_ITEMS.find((item) => location.pathname.startsWith(item.path))?.label ?? 'Profile'}
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                        <ButtonBase sx={{width: 32, height: 32, borderRadius: tokens.radius.badge, border: `1px solid ${tokens.color.neutral200}`}}>
                            <Box component="img" src={bellIcon} alt="Notifications" sx={{width: 16, height: 16}} />
                        </ButtonBase>
                        <ButtonBase sx={{width: 32, height: 32, borderRadius: tokens.radius.badge, border: `1px solid ${tokens.color.neutral200}`}}>
                            <Box component="img" src={profileIcon} alt="Profile" sx={{width: 16, height: 16}} />
                        </ButtonBase>
                    </Stack>
                </Stack>

                <Box component="main" sx={{flex: 1, backgroundColor: '#fafafa', display: 'flex', justifyContent: 'center', py: 3}}>
                    <Box sx={{width: '100%', maxWidth: location.pathname.startsWith('/settings/members') ? 'none' : '962px', px: 3}}>
                        <Outlet />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default SettingsLayout;
